if (!customElements.get('td-recently-viewed')) {
  class TDRecentlyViewedStorage {
    static #STORAGE_KEY = 'viewedProducts';
    static #MAX_PRODUCTS = 4;

    static configure(maxProducts) {
      this.#MAX_PRODUCTS = maxProducts;
      const viewedProducts = this.getProducts().slice(0, maxProducts);
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    }

    static addProduct(productId, variantId, color, productUrl) {
      if (!productId) return;

      let viewedProducts = this.getProducts();
      const normalizedProductId = String(productId);
      const normalizedVariantId = String(variantId || '');
      viewedProducts = viewedProducts.filter((entry) => String(entry.productId) !== normalizedProductId);
      viewedProducts.unshift({
        productId: normalizedProductId,
        variantId: normalizedVariantId,
        color: color || '',
        url: productUrl || ''
      });
      viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    }

    static clear() {
      localStorage.removeItem(this.#STORAGE_KEY);
    }

    static getProducts() {
      const seenProductIds = new Set();
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]').map((entry) => {
        if (typeof entry === 'object' && entry !== null) {
          return {
            productId: String(entry.productId || ''),
            variantId: String(entry.variantId || ''),
            color: entry.color || '',
            url: entry.url || ''
          };
        }

        return { productId: String(entry), variantId: '', color: '', url: '' };
      }).filter((entry) => {
        if (!entry.productId || seenProductIds.has(entry.productId)) return false;
        seenProductIds.add(entry.productId);
        return true;
      });
    }
  }

  class TDRecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.limit = Number(this.dataset.tdRecentlyViewedLimit);
      this.searchUrl = this.dataset.tdRecentlyViewedSearchUrl;
      this.currentProductId = this.dataset.tdRecentlyViewedProductId;
      this.currentProductUrl = this.dataset.tdRecentlyViewedProductUrl;
      this.currentVariantId = this.dataset.tdRecentlyViewedVariantId;
      this.currentColor = this.dataset.tdRecentlyViewedColor;
      this.colorOptionIndex = this.dataset.tdRecentlyViewedColorOptionIndex === ''
        ? -1
        : Number(this.dataset.tdRecentlyViewedColorOptionIndex);
      this.productList = this.querySelector('[data-td-recently-viewed-products]');
      this.carousel = this.querySelector('carousel-slider');
      this.clearButton = this.querySelector('[data-td-recently-viewed-clear]');
      this.content = this.querySelector('[data-td-recently-viewed-content]');
      this.sentinel = this.querySelector('[data-td-recently-viewed-sentinel]');
      this.abortController = new AbortController();
      this.boundVariantChange = this.handleVariantChange.bind(this);
      this.boundClear = this.clear.bind(this);
      this.boundIntersect = this.handleIntersect.bind(this);
      this.loadStarted = false;

      TDRecentlyViewedStorage.configure(this.limit + 1);
      TDRecentlyViewedStorage.addProduct(this.currentProductId, this.currentVariantId, this.currentColor, this.currentProductUrl);
      document.addEventListener('on:variant:change', this.boundVariantChange);
      this.clearButton?.addEventListener('click', this.boundClear);

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(this.boundIntersect);
        this.intersectionObserver.observe(this.sentinel);
      } else {
        this.handleIntersect([{ isIntersecting: true }]);
      }
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
      document.removeEventListener('on:variant:change', this.boundVariantChange);
      this.clearButton?.removeEventListener('click', this.boundClear);
    }

    clear() {
      TDRecentlyViewedStorage.clear();
      this.abortController.abort();
      this.productList.replaceChildren();
      this.content.setAttribute('hidden', '');
      this.sentinel.removeAttribute('hidden');
    }

    handleIntersect(entries) {
      if (this.loadStarted || !entries[0]?.isIntersecting) return;

      this.loadStarted = true;
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      this.loadProducts();
    }

    handleVariantChange(event) {
      if (event.detail.form?.dataset.productId !== this.currentProductId || !event.detail.variant) return;

      this.currentVariantId = String(event.detail.variant.id);
      this.currentColor = this.colorOptionIndex >= 0
        ? event.detail.selectedOptions?.[this.colorOptionIndex] || ''
        : '';
      TDRecentlyViewedStorage.addProduct(this.currentProductId, this.currentVariantId, this.currentColor, this.currentProductUrl);
    }

    async loadProducts() {
      const entries = TDRecentlyViewedStorage
        .getProducts()
        .filter((entry) => entry.productId !== this.currentProductId)
        .slice(0, this.limit);
      if (entries.length === 0) return;

      try {
        const resolvedEntries = await this.resolveProductUrls(entries);
        const products = await Promise.all(resolvedEntries.map((entry) => this.fetchProductCard(entry)));

        this.productList.replaceChildren();
        products.filter(Boolean).forEach((product) => {
          if (this.carousel) {
            const slide = document.createElement('div');
            slide.className = 'slider__item';
            slide.append(product);
            this.productList.append(slide);
          } else {
            this.productList.append(product);
          }
        });

        if (this.productList.children.length === 0) return;

        this.content.removeAttribute('hidden');
        this.sentinel.setAttribute('hidden', '');
        this.carousel?.refresh();
      } catch (error) {
        if (error.name !== 'AbortError') console.warn(error);
      }
    }

    async resolveProductUrls(entries) {
      const unresolvedEntries = entries.filter((entry) => !entry.url);
      if (unresolvedEntries.length === 0) return entries;

      const searchUrl = new URL(this.searchUrl, window.location.origin);
      searchUrl.searchParams.set('q', unresolvedEntries.map((entry) => `id:${entry.productId}`).join(' OR '));
      searchUrl.searchParams.set('section_id', 'predictive-search');
      searchUrl.searchParams.set('resources[type]', 'product');
      searchUrl.searchParams.set('resources[limit]', unresolvedEntries.length);

      const response = await fetch(searchUrl, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Recently viewed URL request failed: ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productsById = new Map(
        [...responseDocument.querySelectorAll('.product-block[data-product-id]')].map((product) => [product.dataset.productId, product])
      );

      return entries.map((entry) => {
        if (entry.url) return entry;

        const productLink = productsById.get(entry.productId)?.querySelector('.product-link');
        if (!productLink) return entry;

        const productUrl = new URL(productLink.href, window.location.origin);
        productUrl.search = '';
        return { ...entry, url: `${productUrl.pathname}${productUrl.hash}` };
      }).filter((entry) => entry.url);
    }

    async fetchProductCard(entry) {
      try {
        const productUrl = new URL(entry.url, window.location.origin);
        if (entry.variantId) productUrl.searchParams.set('variant', entry.variantId);
        productUrl.searchParams.set('section_id', 'td-recently-viewed-card');

        const response = await fetch(productUrl, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Recently viewed card request failed: ${response.status}`);

        const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        return responseDocument.querySelector('.product-block[data-product-id]');
      } catch (error) {
        if (error.name !== 'AbortError') console.warn(error);
        return null;
      }
    }
  }

  customElements.define('td-recently-viewed', TDRecentlyViewed);
}
